import React, { useState, useEffect } from 'react';
import { Save, X, RefreshCw } from 'lucide-react';
import websiteApi from '../../services/websiteApi';
import './PaymentSettingsManager.css';

interface PaymentSettingsData {
  _id?: string;
  paymentsEnabled: boolean;
  maintenanceMode: boolean;
  maintenanceMessage: string;
  paymentDisabledMessage: string;
  allowedPlans: string[];
}

const PaymentSettingsManager: React.FC = () => {
  const [settings, setSettings] = useState<PaymentSettingsData>({
    paymentsEnabled: true,
    maintenanceMode: false,
    maintenanceMessage: 'We are currently performing maintenance. Please try again later.',
    paymentDisabledMessage: 'Payment processing is currently disabled. Please contact support for assistance.',
    allowedPlans: ['Free', 'Basic', 'Pro'],
  });
  const [originalSettings, setOriginalSettings] = useState<PaymentSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const existingSettings = await websiteApi.getPaymentSettings();
      if (existingSettings) {
        setSettings(existingSettings as PaymentSettingsData);
        setOriginalSettings(existingSettings as PaymentSettingsData);
      }
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const changedFields: Partial<PaymentSettingsData> = {};

      if (originalSettings) {
        (Object.keys(settings) as Array<keyof PaymentSettingsData>).forEach((key) => {
          if (JSON.stringify(settings[key]) !== JSON.stringify(originalSettings[key])) {
            (changedFields as Record<string, unknown>)[key] = settings[key];
          }
        });
      } else {
        Object.assign(changedFields, settings);
      }

      if (Object.keys(changedFields).length === 0) {
        setSuccess('No changes to save');
        setTimeout(() => setSuccess(''), 3000);
        return;
      }

      const response = await websiteApi.updatePaymentSettings(changedFields);
      const savedSettings = (response.settings || response) as PaymentSettingsData;

      setSettings(savedSettings);
      setOriginalSettings(savedSettings);
      setSuccess('Settings saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (field: 'paymentsEnabled' | 'maintenanceMode') => {
    setSettings((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleInputChange = (field: keyof PaymentSettingsData, value: string) => {
    if (field === 'allowedPlans') {
      setSettings((prev) => ({
        ...prev,
        allowedPlans: value.split(',').map((s) => s.trim()).filter(Boolean),
      }));
    } else {
      setSettings((prev) => ({ ...prev, [field]: value }));
    }
  };

  if (loading) {
    return (
      <div className="ps-page ps-loading">
        <RefreshCw size={20} className="ps-spin" />
        Loading settings…
      </div>
    );
  }

  return (
    <div className="ps-page">
      <div className="ps-toolbar">
        <span className="ps-toolbar-note">Control website payments and checkout messages</span>
        <div className="ps-toolbar-actions">
          <button type="button" className="ps-btn" onClick={fetchSettings}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button type="button" className="ps-btn ps-btn--primary" onClick={handleSave} disabled={saving}>
            <Save size={14} /> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {success && (
        <div className="ps-toast ps-toast--ok">
          <span>{success}</span>
          <button type="button" onClick={() => setSuccess('')} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}
      {error && (
        <div className="ps-toast ps-toast--err">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}

      <div className="ps-main">
        <div className="ps-panel">
          <div className="ps-panel-head">Status</div>
          <div className="ps-panel-body">
            <div className={`ps-toggle ${settings.paymentsEnabled ? 'ps-toggle--on' : ''}`}>
              <div className="ps-toggle-text">
                <strong>Payments enabled</strong>
                <span>Allow checkout on the website</span>
              </div>
              <button
                type="button"
                className={`ps-switch ${settings.paymentsEnabled ? 'ps-switch--on' : ''}`}
                onClick={() => handleToggle('paymentsEnabled')}
                aria-label="Toggle payments"
              >
                <span className="ps-switch-handle" />
              </button>
            </div>
            <div className={`ps-toggle ${settings.maintenanceMode ? 'ps-toggle--warn' : ''}`}>
              <div className="ps-toggle-text">
                <strong>Maintenance mode</strong>
                <span>Block payments and show maintenance message</span>
              </div>
              <button
                type="button"
                className={`ps-switch ${settings.maintenanceMode ? 'ps-switch--warn' : ''}`}
                onClick={() => handleToggle('maintenanceMode')}
                aria-label="Toggle maintenance mode"
              >
                <span className="ps-switch-handle" />
              </button>
            </div>
          </div>
        </div>

        <div className="ps-panel">
          <div className="ps-panel-head">Messages &amp; plans</div>
          <div className="ps-panel-body">
            <div className="ps-field">
              <label htmlFor="ps-maintenance">Maintenance message</label>
              <textarea
                id="ps-maintenance"
                value={settings.maintenanceMessage}
                onChange={(e) => handleInputChange('maintenanceMessage', e.target.value)}
                rows={3}
              />
            </div>
            <div className="ps-field">
              <label htmlFor="ps-disabled">Disabled payments message</label>
              <textarea
                id="ps-disabled"
                value={settings.paymentDisabledMessage}
                onChange={(e) => handleInputChange('paymentDisabledMessage', e.target.value)}
                rows={3}
              />
            </div>
            <div className="ps-field">
              <label htmlFor="ps-plans">Allowed plans</label>
              <input
                id="ps-plans"
                type="text"
                value={settings.allowedPlans.join(', ')}
                onChange={(e) => handleInputChange('allowedPlans', e.target.value)}
                placeholder="Free, Basic, Pro, Enterprise"
              />
              {settings.allowedPlans.length > 0 && (
                <div className="ps-tags">
                  {settings.allowedPlans.map((plan) => (
                    <span key={plan} className="ps-tag">{plan}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSettingsManager;
